<template>
    <div id="add-ereceipt-modal">
        <b-modal id="modal-add-code"
                 ref="modal"
                 title="Přidat kód"
                 @hidden="resetModal"
                 @ok="handleOk"
                 @shown="shownAddCodeFocus"
                 :ok-disabled="okDisabled || isValidating">

            <template v-slot:modal-cancel>
                Storno
            </template>

            <template v-slot:modal-header="{ close }">
                <div id="cameraButtonWrapper" class="d-flex align-items-center">
                    <!-- Kamera -->
                    <b-button id="cameraButton" size="sm"
                              :variant="showCamera ? 'danger' : 'info'"
                              class="mr-2"
                              @click="switchCamera"
                              :disabled="isCameraEnabled && disabledCameraText">
                        <i class="fa fa-camera"></i>
                    </b-button>

                    <!-- Galerie (vlastní input + tlačítko) -->
                    <input ref="fileInput"
                           type="file"
                           accept="image/*"
                           @change="onFileChange"
                           style="display:none" />
                    <b-button id="galleryButton"
                              size="sm"
                              variant="secondary"
                              class="mr-2"
                              @click="openFilePicker">
                        <i class="fa fa-image"></i>
                        Galerie
                    </b-button>

                    <span class="text-muted" v-if="selectedFileName">{{ selectedFileName }}</span>
                </div>

                <b-tooltip target="cameraButtonWrapper" triggers="hover" v-if="!isCameraEnabled && disabledCameraText">
                    {{disabledCameraText}}
                </b-tooltip>
                <h5 class="mb-0">Přidat kód</h5>
            </template>

            <form ref="form" @submit.stop.prevent="handleSubmit">
                <b-form-group :state="validationState"
                              label="Kód"
                              label-for="name-input"
                              :invalid-feedback="validationMessage">

                    <!--
                        Validace se spouští z watcheru nad addEItemCode (tj. z události input),
                        ne z keyup - jen tak se zachytí i vložení kódu ze schránky.
                        Na mobilu "Vložit" z kontextového menu žádné keyup nevyvolá.
                    -->
                    <b-form-input id="eReceiptCode" ref="addCodeInput"
                                  v-model="$v.addEItemCode.$model"
                                  :state="validationState"
                                  autocomplete="off"
                                  autocapitalize="characters"
                                  autocorrect="off"
                                  spellcheck="false"
                                  :formatter="formatter"></b-form-input>
                    <small class="text-muted" v-if="isValidating">Ověřuji kód...</small>
                    <span class="text-danger">{{disabledCameraText}}</span>
                </b-form-group>
            </form>

            <StreamBarcodeReader v-if="showCamera" @decode="onDecode" @loaded="onInit">
                <div class="loading-indicator text-center" v-if="cameraLoading">
                    <strong>Zapínám kameru...</strong>
                </div>
            </StreamBarcodeReader>
        </b-modal>
    </div>
</template>

<script>
    import { required } from "vuelidate/lib/validators";
    import { eitemCodeValidatorService } from '@/services'
    import { StreamBarcodeReader } from "vue-barcode-reader";
    // ZXing načteme až při použití (dynamic import), aby se zbytečně nezvětšoval bundle

    var DetectRTC = require('detectrtc');

    // Prodleva, po kterou se čeká na doťukání kódu, než se pošle dotaz na server
    const VALIDATION_DEBOUNCE_MS = 300;

    export default {
        name: 'AddItemCodeModal',
        components: {
            StreamBarcodeReader
        },
        data: function () {
            return {
                addEItemCode: "",
                validationState: null,
                validationMessage: "",
                modalFormValidFromServer: false,
                showCamera: false,
                isCameraEnabled: false,
                disabledCameraText: null,
                cameraLoading: false,
                okDisabled: true,
                selectedFileName: "",
                isValidating: false,
                validationTimer: null,
                validationRequestId: 0
            }
        },
        props: {
            eDocuments: {
                default: () => [],
                type: Array,
                required: false
            },
            eReceiptIsAllowed: {
                required: false
            },
            eVoucherIsAllowed: {
                required: false
            }
        },
        validations: {
            addEItemCode: {
                required,
                lengthValidity(value) {
                    const codes = this.splitCodes(value);
                    // 9 eVoucher / 12 ePrescription
                    return codes.length > 0 && codes.every(code => code.length === 9 || code.length === 12);
                },
                isServerValid(value) {
                    return this.modalFormValidFromServer
                }
            }
        },
        watch: {
            // Reaguje na jakoukoli změnu hodnoty - psaní, vložení ze schránky,
            // našeptávání i programové naplnění po načtení QR kódu.
            addEItemCode() {
                this.scheduleValidation()
            }
        },
        methods: {
            // Kód se běžně sdílí s mezerami ("POQ GTJ 8KL B9W"), pomlčkami nebo s prefixem "e.".
            // Všechno tohle zahodíme, oddělovačem více kódů zůstává čárka.
            normalizeCodes(value) {
                return String(value === null || value === undefined ? '' : value)
                    .toUpperCase()
                    .replace(/(^|,)\s*E\.\s*/g, '$1')
                    .replace(/[^A-Z0-9,]/g, '');
            },
            splitCodes(value) {
                return this.normalizeCodes(value)
                    .split(',')
                    .filter(code => code.length > 0);
            },
            formatter(value) {
                return this.normalizeCodes(value)
            },
            // StreamBarcodeReader emituje 'loaded' bez parametru (na rozdíl od vue-qrcode-reader),
            // takže tady jen zhasneme indikátor načítání. Chyby řeší switchCamera.
            onInit() {
                this.cameraLoading = false
            },
            handleError(error) {
                let errorTypes = {
                    NotAllowedError: "CHYBA: musíte udělit oprávnění pro přístup ke kameře",
                    NotFoundError: "CHYBA: zařízení nemá žádnou kameru",
                    NotSupportedError: "CHYBA: vyžadován zabezpečený kontext (HTTPS, localhost)",
                    NotReadableError: "CHYBA: zkontrolujte, zda se kamera již nepoužívá",
                    OverconstrainedError: "CHYBA: vaše kamera je nevhodná",
                    StreamApiNotSupportedError: "CHYBA: Stream API není v tomto prohlížeči podporován"
                }
                this.disabledCameraText = errorTypes[error.name] || "Neznámá chyba"
            },
            resetModal() {
                this.cancelValidation()
                this.addEItemCode = ''
                this.validationState = null
                this.validationMessage = ""
                this.modalFormValidFromServer = false
                this.selectedFileName = ""
                this.okDisabled = true
                this.$v.addEItemCode.$reset()
            },
            shownAddCodeFocus() {
                this.$refs.addCodeInput && this.$refs.addCodeInput.focus()
                this.okDisabled = true;
            },

            // --- VALIDACE ---

            // Zruší naplánovanou validaci i výsledek té, která právě běží na serveru.
            cancelValidation() {
                if (this.validationTimer) {
                    clearTimeout(this.validationTimer)
                    this.validationTimer = null
                }
                this.validationRequestId++
                this.isValidating = false
            },
            scheduleValidation() {
                this.cancelValidation()
                this.modalFormValidFromServer = false
                this.okDisabled = true

                if (!this.addEItemCode) {
                    this.validationState = null
                    this.validationMessage = ""
                    return
                }

                this.validationTimer = setTimeout(() => { this.validateCodes() }, VALIDATION_DEBOUNCE_MS)
            },
            async validateCodes() {
                this.validationTimer = null

                // Každý běh má své číslo - odpověď staršího dotazu už výsledek nepřepíše.
                const requestId = ++this.validationRequestId
                const codes = this.splitCodes(this.addEItemCode)

                this.modalFormValidFromServer = false

                if (codes.length === 0) {
                    this.setValidationMessage("Kód musí být vyplněný.", false)
                    return
                }

                this.isValidating = true

                try {
                    let valid = true

                    for (let code of codes) {
                        if (code.length === 9) {
                            if (!this.checkEVoucher(code)) {
                                valid = false;
                                break;
                            }
                        } else if (code.length === 12) {
                            if (!this.checkEReceipt(code)) {
                                valid = false;
                                break;
                            }
                        }
                        else {
                            this.setValidationMessage("Kód musí mít 9 nebo 12 znaků.", false);
                            valid = false;
                            break;
                        }

                        if (this.isCodeAlreadyAdded(code)) {
                            this.setValidationMessage("Kód " + code + " je již na seznamu.", false);
                            valid = false;
                            break;
                        }

                        let result = await eitemCodeValidatorService.isEItemCodeValid(code);

                        // Mezitím se vstup změnil, tenhle výsledek už neplatí.
                        if (requestId !== this.validationRequestId) {
                            return
                        }

                        if (!result.data.isValid) {
                            this.setValidationMessage("Kód: " + code + " není validní", false);
                            valid = false;
                            break;
                        }
                    }

                    this.modalFormValidFromServer = valid;

                    this.$v.addEItemCode.$touch();

                    if (valid) {
                        this.setValidationMessage(codes.length > 1 ? "Všechny kódy jsou validní." : "Kód je validní.", true);
                    }

                    this.validationState = valid;
                } catch (e) {
                    if (requestId !== this.validationRequestId) {
                        return
                    }

                    console.error("Ověření kódu selhalo", e);
                    this.setValidationMessage("Kód se nepodařilo ověřit, zkuste to prosím znovu.", false);
                } finally {
                    if (requestId === this.validationRequestId) {
                        this.isValidating = false
                    }
                }
            },

            // --- ODESLÁNÍ ---

            async handleOk(bvModalEvt) {
                bvModalEvt.preventDefault()
                await this.handleSubmit()
            },
            async handleSubmit() {
                this.cancelValidation();
                this.okDisabled = true;

                let codes = this.splitCodes(this.addEItemCode);

                if (codes.length === 0) {
                    this.setValidationMessage("Kód musí být vyplněný.", false);
                    return;
                }

                try {
                    for (let code of codes) {
                        let added = await this.validateAndAddCode(code);

                        // Chybová hláška je už nastavená, modál necháme otevřený.
                        if (!added) {
                            return;
                        }
                    }

                    this.$nextTick(() => {
                        this.$bvModal.hide('modal-add-code')
                    })
                } catch (e) {
                    console.error("Chyba", e);
                    this.setValidationMessage("Kód se nepodařilo ověřit, zkuste to prosím znovu.", false);
                }
            },
            async validateAndAddCode(code) {
                if (this.isCodeAlreadyAdded(code)) {
                    this.setValidationMessage("Kód " + code + " je již na seznamu.", false)
                    return false
                }

                let result = await this.checkFormValidity(code);

                if (!result.isValid) {
                    return false
                }

                this.eDocuments.push({ code: code, eItemShortcut: result.eItemShortcut });

                return true
            },
            isCodeAlreadyAdded(code) {
                return this.eDocuments.some(item => item.code === code)
            },
            async checkFormValidity(code) {
                this.okDisabled = true;
                this.$v.addEItemCode.$touch();

                let result = await eitemCodeValidatorService.isEItemCodeValid(code);
                this.modalFormValidFromServer = result.data.isValid;
                let valid = result.data.isValid && !this.$v.addEItemCode.$invalid

                if (!valid) {
                    this.setValidationMessageByError()
                }
                else {
                    this.okDisabled = false;
                }

                this.validationState = valid

                return result.data
            },
            setValidationMessageByError() {
                if (!this.$v.addEItemCode.required) {
                    this.setValidationMessage("Kód musí být vyplněný.", false)
                }
                else if (!this.$v.addEItemCode.lengthValidity) {
                    this.setValidationMessage("Délka kódu neodpovídá ePoukazu ani eReceptu.", false)
                }
                else if (!this.$v.addEItemCode.isServerValid) {
                    this.setValidationMessage("Kód není validní.", false)
                }
            },
            setValidationMessage(message, valid) {
                this.validationMessage = message
                this.validationState = valid
                this.okDisabled = !valid;
            },
            checkEVoucher(code) {
                if (code.length === 9 && !this.eVoucherIsAllowed) {
                    this.setValidationMessage("Vámi vybraná provozovna nepodporuje ePoukazy.", false)
                    return false;
                }

                return true;
            },
            checkEReceipt(code) {
                if (code.length === 12 && !this.eReceiptIsAllowed) {
                    this.setValidationMessage("Vámi vybraná provozovna nepodporuje eRecepty.", false)
                    return false;
                }

                return true;
            },

            // --- DEKÓDOVÁNÍ ---

            // Výsledek z kamery
            async onDecode(result) {
                await this.handleDecoded(result);
            },

            // Společná logika pro parsování a následnou validaci
            async handleDecoded(result) {
                this.showCamera = false;

                let code;

                if (typeof result === 'string' && result.startsWith("https") && result.includes('erp?')) {
                    code = result.substring(
                        result.lastIndexOf("i=") + 2,
                        result.lastIndexOf("&d"))
                }
                else if (typeof result === 'string' && result.startsWith("https") && result.includes('epo?')) {
                    code = result.substring(
                        result.lastIndexOf("i=") + 2,
                        result.lastIndexOf("&s"))
                }
                else if (typeof result === 'string' && result.startsWith("PRUV")) {
                    const pattern = /SERP:(?<erecept>.{12})\\*/gm;
                    const matches = result.match(pattern) || [];
                    const mapped = matches.map(m => m.replace("SERP:", ""));
                    code = mapped.join(',');
                }
                else {
                    // Obecný fallback (1D/QR text, nebo více kódů spojených čárkou)
                    code = String(result);
                }

                // Programové naplnění neprojde přes formatter, normalizujeme ručně.
                this.addEItemCode = this.normalizeCodes(code);

                await this.$nextTick();

                this.cancelValidation();
                await this.validateCodes();
            },

            // --- GALERIE (vlastní implementace s ZXing) ---
            openFilePicker() {
                this.$refs.fileInput && this.$refs.fileInput.click();
            },
            async onFileChange(e) {
                const file = e.target.files && e.target.files[0];
                this.selectedFileName = file ? file.name : "";

                if (!file) return;

                try {
                    const url = URL.createObjectURL(file);

                    // Načti ZXing až teď
                    const { BrowserMultiFormatReader, NotFoundException } = await import(/* webpackChunkName: "zxing" */ '@zxing/library');

                    const reader = new BrowserMultiFormatReader();
                    // decodeFromImageUrl vrátí první nalezený kód (1D i QR)
                    const result = await reader.decodeFromImageUrl(url);

                    URL.revokeObjectURL(url);

                    await this.handleDecoded(result.getText ? result.getText() : String(result));
                } catch (err) {
                    console.error(err);
                    this.setValidationMessage("Nepodařilo se přečíst kód z obrázku.", false);
                } finally {
                    // vyresetujeme input, aby šlo vybrat znovu ten samý soubor
                    if (this.$refs.fileInput) this.$refs.fileInput.value = '';
                }
            },

            // --- KAMERA ---

            async switchCamera() {
                if (this.showCamera) {
                    this.showCamera = false;
                    this.cameraLoading = false;
                    return;
                }

                this.disabledCameraText = null;

                if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                    this.isCameraEnabled = false;
                    this.handleError({ name: 'NotSupportedError' });
                    return;
                }

                this.cameraLoading = true;

                try {
                    // O oprávnění si řekneme sami a počkáme na odpověď uživatele.
                    // Čtečku mountujeme až se souhlasem, takže se kamera otevře
                    // hned po potvrzení dialogu, ne až na druhé kliknutí.
                    let stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
                    stream.getTracks().forEach(track => track.stop());

                    this.isCameraEnabled = true;
                    this.showCamera = true;
                } catch (error) {
                    this.isCameraEnabled = false;
                    this.showCamera = false;
                    this.cameraLoading = false;
                    this.handleError(error);
                }
            },
            checkIfCameraAvailable() {
                DetectRTC.load(() => {
                    this.isCameraEnabled = DetectRTC.isWebRTCSupported && DetectRTC.hasWebcam;
                    if (!this.isCameraEnabled) {
                        this.disabledCameraText = 'Kamera není dostupná';
                    }
                });
            },
        },
        beforeDestroy() {
            this.cancelValidation()
        },
        created() {
            // this.checkIfCameraAvailable();
        }
    }
</script>
